import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { createGetTableActions, getAccountingToolbarIcon } from '../shared/tableActions';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { AccAccountType } from './useData';

export const moduleConfig = {
  module: {
    title: 'Account types',
    permissionModuleKey: 'acc_account_types',
    route: {
      list: '/accounting/account-types',
      new: '/accounting/account-types/new',
      detail: (id: number) => `/accounting/account-types/${id}`,
      edit: (id: number) => `/accounting/account-types/${id}/edit`},
    api: { list: '/acc-account-types', detail: (id: number) => `/acc-account-types/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by name or code…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: AccAccountType[]) => items ,
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New account type',
        showTitle: false,
        variant: 'primary' as const,
        href: '/accounting/account-types/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: AccAccountType) => item.id,
  },
  emptyStates: {
    noData: 'Create FS classification account types (Asset, Liability, Revenue…).',
    noSearchResults: 'No account types match your search.',
    noFilterResults: 'No account types match the filters.',
  },
  delete: {
    title: 'Delete account type',
    message: 'Delete this account type? Accounts using it will keep their data but lose the type link.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete account type. It may be assigned to accounts.',
    handler: async (id: number) => {
      const { deleteAccAccountType } = await import('../../../services/mutations');
      await deleteAccAccountType(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Account type',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This account type does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Account type',
    getTitle: (item: AccAccountType) => (item.name ? item.name : `Type #${item.id}`)},
  form: {
    new: {
      title: 'New account type',
      subtitle: 'FS classification (Asset, Liability, Revenue…) for chart grouping and reporting',
      submitText: 'Create'},
    edit: {
      title: 'Edit account type',
      subtitle: 'Update code, display order, and availability on the COA form',
      submitText: 'Save'},
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formSizing: 'content' as const,
    formMaxWidth: 'max-w-3xl',
    create: async (data: Partial<AccAccountType>) => {
      const { createAccAccountType } = await import('../../../services/mutations');
      return createAccAccountType(data);
    },
    update: async (id: number, data: Partial<AccAccountType>) => {
      const { updateAccAccountType } = await import('../../../services/mutations');
      return updateAccAccountType(id, data);
    }},
  fields: [
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'e.g. ASSET, LIABILITY, REVENUE',
      helperText: 'FS classification code used for chart grouping and reporting.',
      validation: { required: true, maxLength: 30 }},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Asset, Liability, Expense',
      helperText: 'Display name shown on the chart of accounts form and reports.',
      validation: { required: true, maxLength: 100 }},
    {
      name: 'sortOrder',
      label: 'Sort order',
      type: 'number',
      placeholder: '0',
      helperText: 'Lower numbers appear first in account type dropdowns.',
      defaultValue: 0,
      validation: { min: 0 }},
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Available for new COA entries',
      description: 'Inactive types are hidden from the chart of accounts form but remain on existing accounts.',
      defaultValue: true},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'identification',
        title: 'Identification',
        description: 'Code and name for this FS classification.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['code', 'name'],
        fieldSpans: {
          code: 'md:col-span-4',
          name: 'md:col-span-8'},
        collapsible: true,
        defaultCollapsed: false},
      {
        id: 'display',
        title: 'Display',
        description: 'Control ordering in selection lists.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['sortOrder'],
        fieldSpans: {
          sortOrder: 'md:col-span-4'},
        collapsible: true,
        defaultCollapsed: false},
      {
        id: 'status',
        title: 'Status',
        description: 'Whether this type can be assigned on new or edited accounts.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['isActive'],
        fieldSpans: {
          isActive: 'md:col-span-12'},
        collapsible: true,
        defaultCollapsed: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | number | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value != null && value !== '' ? value : '-'}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<AccAccountType>[] => [
  { key: 'code', header: 'Code', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'sortOrder', header: 'Order', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => (
      <Badge variant={v ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: AccAccountType, formatDate: (s?: string) => string) => [
  {
    title: 'Identification',
    fields: [
      { label: 'Code', value: item.code },
      { label: 'Name', value: item.name },
    ]},
  {
    title: 'Display & status',
    fields: [
      { label: 'Sort order', value: String(item.sortOrder ?? 0) },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
];

const StatusBadges: React.FC<{ item: AccAccountType }> = ({ item }) => (
  <Badge variant={item.isActive ? 'success' : 'danger'} className="ml-2">
    {item.isActive ? 'Active' : 'Inactive'}
  </Badge>
);

export const getTableActions = createGetTableActions<AccAccountType>(moduleConfig.module.route);

export const getToolbarIcon = getAccountingToolbarIcon;

export const getStatusBadges = (item: AccAccountType) => <StatusBadges item={item} />;
export const getInitialFormData = (): Partial<AccAccountType> => ({
  code: '',
  name: '',
  sortOrder: 0,
  isActive: true});
export const mapItemToFormData = (item: AccAccountType): Partial<AccAccountType> => ({
  code: item.code,
  name: item.name,
  sortOrder: item.sortOrder ?? 0,
  isActive: item.isActive ?? true});
